/**
 * หน้าจอ Tool/Die Master (`WO-P2-M11-010`, D-108 ข้อ 4 และ 5, P2-11d) — หน้าจอ
 * สุดท้ายของ Phase 2
 *
 * ปิดสิบเส้นทางที่ `WO-P2-M11-009` เปิดไว้: `GET`/`POST /v1/tools`, `GET`/`PATCH`
 * หนึ่งตัว และสามคู่ list/add ของ products, work-centers, maintenance-events
 *
 * โครงยืมมาจาก `NcrCaseScreen` (ช่องกรองบนสุด ตารางรายการ ฟอร์มอยู่หลังปุ่มในกล่อง
 * `Modal`) บวกแถวที่ขยายได้เพื่อแสดงรายละเอียดของแต่ละเครื่องมือ เพราะทั้งสอง
 * ความสัมพันธ์ที่นี่เป็น many-to-many และต้องแสดงเป็นรายการ ไม่ใช่ฟิลด์เดียว
 *
 * ห้าข้อที่หน้าจอนี้ต้องพูดออกมาให้ดัง ไม่ใช่ซ่อนไว้ (invariant ของ WO นี้):
 *
 * 1. **THE TWO-COUNTER HEADLINE** — สะสมทั้งชีวิตกับนับตั้งแต่ซ่อมบำรุงล่าสุดเป็น
 *    สองคอลัมน์แยกกัน มีป้ายกำกับต่างกันเสมอ (`toolLifeHeadline`, `toolingView.ts`)
 * 2. **NO BROWSER ARITHMETIC** — หน้าจอนี้ไม่บวก/ลบ/สะสมตัวเลขจากสองตัวนับเลย แสดง
 *    ค่าที่เซิร์ฟเวอร์ส่งมาตรง ๆ ทุกครั้งที่โหลดรายการใหม่
 * 3. **RATED LIFE ไม่ใช่ด่าน** — `ratedLifeStatus` ให้แค่ป้ายบอกความสัมพันธ์ ไม่มี
 *    ปุ่มไหนถูกบล็อกเพราะเครื่องมือเกินเรตติ้ง
 * 4. **ENTERPRISE-WIDE** — ไม่มีช่องกรองโรงงาน และตัวกรองเดียวคือชนิดเครื่องมือ
 * 5. **สองความสัมพันธ์เป็นรายการเสมอ** — ส่วนประกอบที่ผลิตได้และเครื่องจักรที่ใช้ได้
 *    แสดงเป็นตารางย่อยของแต่ละเครื่องมือ ไม่ใช่ฟิลด์เดียว
 *
 * การ gate สิทธิ์: `PRODUCT_MASTER` + `VIEW` เป็นสิทธิ์ของ **การเปิดหน้าจอนี้** (ด่าน
 * อยู่ที่ route) ส่วน `CREATE` (ลงทะเบียนเครื่องมือใหม่) และ `EDIT` (แก้ข้อมูล/เพิ่ม
 * การเชื่อมโยง/บันทึกซ่อมบำรุง) เป็นสิทธิ์ของ **ปุ่มที่เขียน** ซึ่งถูกบังคับข้างในนี้
 * ทีละปุ่ม ตรงกับด่านจริงฝั่งเซิร์ฟเวอร์ทีละเส้น
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { Fragment, useCallback, useEffect, useState } from 'react';

import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { Modal } from '../components/Modal';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  createTool,
  fetchToolMaintenanceEvents,
  fetchTools,
  fetchToolProducts,
  fetchToolWorkCenters,
  linkToolProduct,
  linkToolWorkCenter,
  patchTool,
  recordToolMaintenance,
  ruleFromFailure,
  type ToolFailure,
} from '../../lib/tooling/client';
import {
  EMPTY_CREATE_TOOL_FIELDS,
  editToolFieldsFromTool,
  ratedLifeStatus,
  ratedLifeStatusLabel,
  ratedLifeStatusTone,
  toCreateToolBody,
  toLinkToolProductBody,
  toLinkToolWorkCenterBody,
  toolLifeHeadline,
  toolTypeLabel,
  toPatchToolBody,
  toRecordToolMaintenanceBody,
  toToolTypeFilter,
  type CreateToolFields,
  type EditToolFields,
} from '../../lib/tooling/toolingView';
import {
  PRODUCT_MASTER_MODULE,
  TOOL_TYPES,
  TOOL_CODE_MAX_LENGTH,
  SPEC_MAX_LENGTH,
  MAINTENANCE_NOTE_MAX_LENGTH,
  type Tool,
  type ToolMaintenanceEvent,
  type ToolProduct,
  type ToolWorkCenter,
} from '../../lib/tooling/types';

/* ------------------------------------------------- หน้าจอความล้มเหลว ----- */

function failureHeading(failure: ToolFailure): string {
  switch (failure.status) {
    case 'unauthenticated':
      return 'ต้องเข้าสู่ระบบก่อนจึงจะใช้หน้าจอนี้ได้';
    case 'forbidden':
      return 'สิทธิ์ชุดนี้ไม่ครอบคลุมคำขอนี้';
    case 'not-found':
      return 'ไม่พบเครื่องมือที่คำขอนี้อ้างถึง';
    case 'conflict':
      return 'สถานะปัจจุบันไม่รับสิ่งที่กด';
    case 'rule-violation':
      return 'คำขอนี้ติดกฎทางธุรกิจ';
    case 'bad-request':
      return 'คำขอนี้ถูกปฏิเสธว่าไม่ถูกต้อง';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${failure.httpStatus}`;
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้';
    case 'invalid-response':
      return 'ข้อมูลจากเซิร์ฟเวอร์ผิดรูปแบบ';
    case 'cancelled':
      return 'ยกเลิกการเรียกข้อมูลแล้ว';
  }
}

function failureDetail(failure: ToolFailure): string {
  const rule = ruleFromFailure(failure);
  switch (failure.status) {
    case 'unauthenticated':
      return 'เซิร์ฟเวอร์ตอบ 401 — เซสชันหมดอายุหรือถูกยกเลิกไปแล้ว';
    case 'forbidden':
      return `เซิร์ฟเวอร์ตอบ 403 — คำขอนี้ต้องการโมดูล ${PRODUCT_MASTER_MODULE} ในระดับที่สูงกว่าที่แผนกของคุณถืออยู่`;
    case 'not-found':
      return 'เซิร์ฟเวอร์ตอบ 404 — เครื่องมือนี้ไม่มีอยู่แล้ว โหลดรายการใหม่แล้วลองอีกครั้ง';
    case 'conflict':
      return `เซิร์ฟเวอร์ตอบ 409${rule === null ? '' : ` พร้อมกฎ ${rule}`}`;
    case 'rule-violation':
      return rule === 'TOOL_FIELD_IS_SERVER_OWNED'
        ? `เซิร์ฟเวอร์ตอบ 400 พร้อมกฎ ${rule} — สองตัวนับชีวิตเป็นของเซิร์ฟเวอร์ ไม่มีฟอร์มใดในหน้าจอนี้กรอกมันได้ การที่มันไปถึงเซิร์ฟเวอร์จึงเป็นบั๊ก แจ้งผู้ดูแลระบบ`
        : `เซิร์ฟเวอร์ตอบ 422${rule === null ? '' : ` พร้อมกฎ ${rule}`} — ไม่ใช่ข้อผิดพลาดของระบบ`;
    case 'bad-request':
      return `เซิร์ฟเวอร์ตอบ 400${rule === null ? '' : ` พร้อมกฎ ${rule}`}`;
    case 'server-error':
      return 'คำขอไปถึงเซิร์ฟเวอร์แล้วแต่ทำงานไม่สำเร็จ';
    case 'network-error':
      return failure.detail;
    case 'invalid-response':
      return 'หน้าจอปฏิเสธที่จะแสดงข้อมูลที่อ่านได้ไม่ครบ';
    case 'cancelled':
      return 'คำขอถูกยกเลิกก่อนได้รับคำตอบ';
  }
}

interface FailureNoticeProps {
  readonly failure: ToolFailure;
  readonly testIdPrefix: string;
}

function ToolFailureNotice({ failure, testIdPrefix }: FailureNoticeProps) {
  const rule = ruleFromFailure(failure);
  const refusedByRule = failure.status === 'rule-violation' || failure.status === 'conflict';
  return (
    <div
      className="cap-notice"
      data-testid={`${testIdPrefix}-failure`}
      data-failure={failure.status}
      data-rule={rule ?? ''}
      role="status"
    >
      <StatusBadge tone={refusedByRule ? 'warning' : 'danger'}>
        {refusedByRule ? 'ติดกฎทางธุรกิจ' : 'ทำรายการไม่สำเร็จ'}
      </StatusBadge>
      <h3 className="cap-notice__title">{failureHeading(failure)}</h3>
      <p className="cap-notice__text">{failureDetail(failure)}</p>
      {rule === null ? null : (
        <p className="cap-notice__text">
          กฎที่ชน: <code className="cap-table__code" data-testid={`${testIdPrefix}-rule`}>{rule}</code>
        </p>
      )}
    </div>
  );
}

/* --------------------------------------------------------------- หน้าจอ -- */

export interface ToolRegistryScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type ListState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly Tool[] }
  | ToolFailure;

type SubResourceState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | ToolFailure;

interface DetailState {
  readonly toolId: string;
  readonly products: SubResourceState<ToolProduct>;
  readonly workCenters: SubResourceState<ToolWorkCenter>;
  readonly maintenanceEvents: SubResourceState<ToolMaintenanceEvent>;
}

export function ToolRegistryScreen({ permissions, apiBaseUrl, fetchImpl }: ToolRegistryScreenProps) {
  const canCreate = can(permissions, PRODUCT_MASTER_MODULE, 'CREATE');
  const canEdit = can(permissions, PRODUCT_MASTER_MODULE, 'EDIT');
  const canWriteAnything = canCreate || canEdit;

  const [items, setItems] = useState<ListState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);

  const [toolTypeFilter, setToolTypeFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createFields, setCreateFields] = useState<CreateToolFields>(EMPTY_CREATE_TOOL_FIELDS);
  const [createFailure, setCreateFailure] = useState<ToolFailure | null>(null);

  const [editTool, setEditTool] = useState<Tool | null>(null);
  const [editFields, setEditFields] = useState<EditToolFields | null>(null);
  const [editFailure, setEditFailure] = useState<ToolFailure | null>(null);

  const [detailToolId, setDetailToolId] = useState<string | null>(null);
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [detailReloadToken, setDetailReloadToken] = useState(0);

  const [productIdInput, setProductIdInput] = useState('');
  const [productLinkFailure, setProductLinkFailure] = useState<ToolFailure | null>(null);

  const [workCenterIdInput, setWorkCenterIdInput] = useState('');
  const [workCenterLinkFailure, setWorkCenterLinkFailure] = useState<ToolFailure | null>(null);

  const [maintenancePerformedAt, setMaintenancePerformedAt] = useState('');
  const [maintenanceNote, setMaintenanceNote] = useState('');
  const [maintenanceFailure, setMaintenanceFailure] = useState<ToolFailure | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setItems((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchTools(
      { ...(toolTypeFilter === '' ? {} : { tool_type: toolTypeFilter }) },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setItems(result.status === 'ready' ? { status: 'ready', items: result.page.items } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken, toolTypeFilter]);

  /* -------------------------------------------------------- ลงทะเบียน --- */

  const openCreate = () => {
    if (!canCreate) {
      return;
    }
    setCreateFields(EMPTY_CREATE_TOOL_FIELDS);
    setCreateFailure(null);
    setCreateOpen(true);
  };

  const closeCreate = useCallback(() => {
    setCreateOpen(false);
  }, []);

  const createBody = toCreateToolBody(createFields);

  const submitCreate = async () => {
    if (createBody === null || busy || !canCreate) {
      return;
    }
    setBusy(true);
    setCreateFailure(null);
    const result = await createTool(createBody, { apiBaseUrl, fetchImpl });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setCreateFailure(result);
      return;
    }
    setCreateOpen(false);
    setReloadToken((token) => token + 1);
  };

  /* -------------------------------------------------------------- แก้ไข -- */

  const openEdit = (tool: Tool) => {
    if (!canEdit) {
      return;
    }
    setEditTool(tool);
    setEditFields(editToolFieldsFromTool(tool));
    setEditFailure(null);
  };

  const closeEdit = useCallback(() => {
    setEditTool(null);
    setEditFields(null);
  }, []);

  const patchBody = editFields === null ? null : toPatchToolBody(editFields);

  const submitEdit = async () => {
    if (editTool === null || patchBody === null || busy || !canEdit) {
      return;
    }
    setBusy(true);
    setEditFailure(null);
    const result = await patchTool(editTool.tool_id, patchBody, { apiBaseUrl, fetchImpl });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setEditFailure(result);
      return;
    }
    setEditTool(null);
    setEditFields(null);
    setReloadToken((token) => token + 1);
  };

  /* ------------------------------------------------------------ รายละเอียด -- */

  const openDetail = (toolId: string) => {
    setProductIdInput('');
    setProductLinkFailure(null);
    setWorkCenterIdInput('');
    setWorkCenterLinkFailure(null);
    setMaintenancePerformedAt('');
    setMaintenanceNote('');
    setMaintenanceFailure(null);
    setDetailToolId((current) => (current === toolId ? null : toolId));
  };

  useEffect(() => {
    if (detailToolId === null) {
      setDetail(null);
      return;
    }
    let active = true;
    setDetail({
      toolId: detailToolId,
      products: { status: 'loading' },
      workCenters: { status: 'loading' },
      maintenanceEvents: { status: 'loading' },
    });

    void fetchToolProducts(detailToolId, { apiBaseUrl, fetchImpl }).then((result) => {
      if (!active) {
        return;
      }
      setDetail((current) =>
        current === null || current.toolId !== detailToolId
          ? current
          : {
              ...current,
              products:
                result.status === 'ready'
                  ? { status: 'ready', items: result.page.items }
                  : result,
            },
      );
    });
    void fetchToolWorkCenters(detailToolId, { apiBaseUrl, fetchImpl }).then((result) => {
      if (!active) {
        return;
      }
      setDetail((current) =>
        current === null || current.toolId !== detailToolId
          ? current
          : {
              ...current,
              workCenters:
                result.status === 'ready'
                  ? { status: 'ready', items: result.page.items }
                  : result,
            },
      );
    });
    void fetchToolMaintenanceEvents(detailToolId, { apiBaseUrl, fetchImpl }).then((result) => {
      if (!active) {
        return;
      }
      setDetail((current) =>
        current === null || current.toolId !== detailToolId
          ? current
          : {
              ...current,
              maintenanceEvents:
                result.status === 'ready'
                  ? { status: 'ready', items: result.page.items }
                  : result,
            },
      );
    });

    return () => {
      active = false;
    };
  }, [apiBaseUrl, fetchImpl, detailToolId, detailReloadToken]);

  const productLinkBody = toLinkToolProductBody(productIdInput);
  const submitLinkProduct = async () => {
    if (detailToolId === null || productLinkBody === null || busy || !canEdit) {
      return;
    }
    setBusy(true);
    setProductLinkFailure(null);
    const result = await linkToolProduct(detailToolId, productLinkBody, { apiBaseUrl, fetchImpl });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setProductLinkFailure(result);
      return;
    }
    setProductIdInput('');
    setDetailReloadToken((token) => token + 1);
  };

  const workCenterLinkBody = toLinkToolWorkCenterBody(workCenterIdInput);
  const submitLinkWorkCenter = async () => {
    if (detailToolId === null || workCenterLinkBody === null || busy || !canEdit) {
      return;
    }
    setBusy(true);
    setWorkCenterLinkFailure(null);
    const result = await linkToolWorkCenter(detailToolId, workCenterLinkBody, {
      apiBaseUrl,
      fetchImpl,
    });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setWorkCenterLinkFailure(result);
      return;
    }
    setWorkCenterIdInput('');
    setDetailReloadToken((token) => token + 1);
  };

  const maintenanceBody = toRecordToolMaintenanceBody(maintenancePerformedAt, maintenanceNote);
  const submitMaintenance = async () => {
    if (detailToolId === null || maintenanceBody === null || busy || !canEdit) {
      return;
    }
    setBusy(true);
    setMaintenanceFailure(null);
    const result = await recordToolMaintenance(detailToolId, maintenanceBody, {
      apiBaseUrl,
      fetchImpl,
    });
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setMaintenanceFailure(result);
      return;
    }
    setMaintenancePerformedAt('');
    setMaintenanceNote('');
    setDetailReloadToken((token) => token + 1);
    // ซ่อมบำรุงตัดเส้นตัวนับ "นับตั้งแต่ซ่อมบำรุงล่าสุด" — โหลดรายการหลักใหม่ด้วย
    // เพื่อให้แถวของเครื่องมือนี้แสดงตัวเลขที่เซิร์ฟเวอร์คำนวณใหม่แล้ว
    setReloadToken((token) => token + 1);
  };

  const listItems = items.status === 'ready' ? items.items : [];

  return (
    <div className="cap-page" data-testid="tool-registry">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            เครื่องมือ / แม่พิมพ์ (Tool/Die Master)
            <InfoHint
              label="เกี่ยวกับทะเบียนเครื่องมือ"
              text={
                <>
                  ทะเบียนเครื่องมือระดับองค์กร — ไม่แยกตามโรงงาน เพราะเครื่องมือที่ย้ายข้าม
                  โรงงานยังเป็นตัวเดียวกัน{' '}
                  <strong>สองตัวนับชีวิตคำนวณจากใบยืนยันงานสดทุกครั้งที่โหลด</strong> ไม่มีค่า
                  ใดถูกเก็บไว้ล่วงหน้า (D-108 ข้อ 5)
                </>
              }
            />
          </h1>
        </div>
      </div>

      {!canWriteAnything ? (
        <div className="cap-notice" data-testid="tool-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังลงทะเบียน/แก้ไขเครื่องมือไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_create</code>{' '}
            หรือ <code className="cap-table__code">can_edit</code> ของโมดูล{' '}
            <code className="cap-table__code">{PRODUCT_MASTER_MODULE}</code> — รายการและตัวกรอง
            ยังใช้ได้
          </p>
        </div>
      ) : null}

      {/* --------------------------------------------------------- ตัวกรอง -- */}

      <div className="cap-filters" data-testid="tool-filter-form">
        <label className="cap-field">
          <span className="cap-field__label">ชนิดเครื่องมือ</span>
          <select
            className="cap-input"
            data-testid="tool-filter-type"
            value={toolTypeFilter}
            onChange={(event) => {
              setToolTypeFilter(toToolTypeFilter(event.target.value) ?? '');
            }}
          >
            <option value="">ทุกชนิด</option>
            {TOOL_TYPES.map((type) => (
              <option key={type} value={type}>
                {toolTypeLabel(type)}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          className="cap-button cap-button--primary"
          data-testid="tool-create-open"
          disabled={!canCreate}
          title={canCreate ? undefined : `ต้องมีสิทธิ์ ${PRODUCT_MASTER_MODULE} + CREATE จึงจะลงทะเบียนเครื่องมือใหม่ได้`}
          onClick={openCreate}
        >
          เพิ่มเครื่องมือ
        </button>
        {canCreate ? null : (
          <span className="cap-field__hint" data-testid="tool-create-open-reason">
            ต้องมีสิทธิ์ {PRODUCT_MASTER_MODULE} + CREATE จึงจะลงทะเบียนเครื่องมือใหม่ได้
          </span>
        )}
      </div>

      {/* --------------------------------------------------------- ลงทะเบียน -- */}

      <Modal open={createOpen} onClose={closeCreate} title="ลงทะเบียนเครื่องมือใหม่">
        <form
          className="cap-form"
          data-testid="tool-create-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">รหัสเครื่องมือ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="tool-create-code"
              value={createFields.toolCode}
              maxLength={TOOL_CODE_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, toolCode: event.target.value }));
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">ชนิด (ไม่บังคับ)</span>
            <select
              className="cap-input"
              data-testid="tool-create-type"
              value={createFields.toolType}
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, toolType: event.target.value }));
              }}
            >
              <option value="">— ยังไม่จำแนก —</option>
              {TOOL_TYPES.map((type) => (
                <option key={type} value={type}>
                  {toolTypeLabel(type)}
                </option>
              ))}
            </select>
          </label>

          <label className="cap-field">
            <span className="cap-field__label">สเปก (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="tool-create-spec"
              value={createFields.spec}
              maxLength={SPEC_MAX_LENGTH}
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({ ...previous, spec: event.target.value }));
              }}
            />
          </label>

          <label className="cap-field">
            <span className="cap-field__label">
              เรตติ้งอายุการใช้งาน (shots, ไม่บังคับ)
              <InfoHint
                label="เกี่ยวกับเรตติ้งอายุการใช้งาน"
                text={
                  <>
                    เป็น<strong>สเปก</strong>ไว้เทียบเท่านั้น ไม่ใช่ด่านที่ระบบบังคับ — เครื่องมือที่
                    เกินเรตติ้งยังใช้งานได้ปกติ
                  </>
                }
              />
            </span>
            <input
              className="cap-input"
              data-testid="tool-create-rated-life"
              value={createFields.ratedLifeShots}
              inputMode="numeric"
              autoComplete="off"
              onChange={(event) => {
                setCreateFields((previous) => ({
                  ...previous,
                  ratedLifeShots: event.target.value,
                }));
              }}
            />
          </label>

          <div className="cap-form__actions">
            <button
              type="submit"
              className="cap-button cap-button--primary"
              data-testid="tool-create-submit"
              disabled={busy || createBody === null}
            >
              {busy ? 'กำลังลงทะเบียน…' : 'ลงทะเบียน'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="tool-create-cancel"
              disabled={busy}
              onClick={closeCreate}
            >
              ยกเลิก
            </button>
          </div>

          {createFailure === null ? null : (
            <ToolFailureNotice failure={createFailure} testIdPrefix="tool-create" />
          )}
        </form>
      </Modal>

      {/* -------------------------------------------------------------- แก้ไข -- */}

      <Modal
        open={editTool !== null}
        onClose={closeEdit}
        title={editTool === null ? 'แก้ไขเครื่องมือ' : `แก้ไขเครื่องมือ — ${editTool.tool_code ?? editTool.tool_id}`}
      >
        {editTool === null || editFields === null ? null : (
          <form
            className="cap-form"
            data-testid="tool-edit-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitEdit();
            }}
          >
            <label className="cap-field">
              <span className="cap-field__label">รหัสเครื่องมือ (เว้นว่าง = ลบค่านี้)</span>
              <input
                className="cap-input"
                data-testid="tool-edit-code"
                value={editFields.toolCode}
                maxLength={TOOL_CODE_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setEditFields((previous) =>
                    previous === null ? previous : { ...previous, toolCode: event.target.value },
                  );
                }}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">ชนิด (เว้นว่าง = ลบค่านี้)</span>
              <select
                className="cap-input"
                data-testid="tool-edit-type"
                value={editFields.toolType}
                onChange={(event) => {
                  setEditFields((previous) =>
                    previous === null ? previous : { ...previous, toolType: event.target.value },
                  );
                }}
              >
                <option value="">— ไม่ระบุ —</option>
                {TOOL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {toolTypeLabel(type)}
                  </option>
                ))}
              </select>
            </label>

            <label className="cap-field">
              <span className="cap-field__label">สเปก (เว้นว่าง = ลบค่านี้)</span>
              <input
                className="cap-input"
                data-testid="tool-edit-spec"
                value={editFields.spec}
                maxLength={SPEC_MAX_LENGTH}
                autoComplete="off"
                onChange={(event) => {
                  setEditFields((previous) =>
                    previous === null ? previous : { ...previous, spec: event.target.value },
                  );
                }}
              />
            </label>

            <label className="cap-field">
              <span className="cap-field__label">เรตติ้งอายุการใช้งาน (shots, เว้นว่าง = ลบค่านี้)</span>
              <input
                className="cap-input"
                data-testid="tool-edit-rated-life"
                value={editFields.ratedLifeShots}
                inputMode="numeric"
                autoComplete="off"
                onChange={(event) => {
                  setEditFields((previous) =>
                    previous === null
                      ? previous
                      : { ...previous, ratedLifeShots: event.target.value },
                  );
                }}
              />
            </label>

            <div className="cap-form__actions">
              <button
                type="submit"
                className="cap-button cap-button--primary"
                data-testid="tool-edit-submit"
                disabled={busy || patchBody === null}
              >
                {busy ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="tool-edit-cancel"
                disabled={busy}
                onClick={closeEdit}
              >
                ยกเลิก
              </button>
            </div>

            {editFailure === null ? null : (
              <ToolFailureNotice failure={editFailure} testIdPrefix="tool-edit" />
            )}
          </form>
        )}
      </Modal>

      {/* --------------------------------------------------------- รายการ --- */}

      {items.status === 'loading' ? (
        <div className="cap-notice" data-testid="tool-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายการเครื่องมือ…</p>
        </div>
      ) : null}

      {items.status !== 'loading' && items.status !== 'ready' ? (
        <ToolFailureNotice failure={items} testIdPrefix="tool-list" />
      ) : null}

      {items.status === 'ready' && listItems.length === 0 ? (
        <div className="cap-notice" data-testid="tool-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีเครื่องมือ</StatusBadge>
          <h2 className="cap-notice__title">ไม่มีเครื่องมือตามตัวกรองนี้</h2>
        </div>
      ) : null}

      {items.status === 'ready' && listItems.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="เครื่องมือทั้งหมด">
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="tool-table">
              <caption className="cap-visually-hidden">
                เครื่องมือพร้อมสองตัวนับชีวิตและความสัมพันธ์กับเรตติ้ง
              </caption>
              <thead>
                <tr>
                  <th scope="col">รหัส</th>
                  <th scope="col">ชนิด</th>
                  <th scope="col">สเปก</th>
                  <th scope="col">เรตติ้ง (shots)</th>
                  {/* THE TWO-COUNTER HEADLINE — สองคอลัมน์แยกกันเสมอ */}
                  <th scope="col">สะสมทั้งชีวิต</th>
                  <th scope="col">นับตั้งแต่ซ่อมบำรุงล่าสุด</th>
                  <th scope="col">ความสัมพันธ์กับเรตติ้ง</th>
                  <th scope="col">การกระทำ</th>
                </tr>
              </thead>
              <tbody>
                {listItems.map((tool) => {
                  const headline = toolLifeHeadline(tool);
                  const status = ratedLifeStatus(tool);
                  return (
                    <Fragment key={tool.tool_id}>
                      <tr data-testid={`tool-row-${tool.tool_id}`}>
                        <td>
                          <code className="cap-table__code">{tool.tool_code ?? '—'}</code>
                        </td>
                        <td>{tool.tool_type === null ? '—' : toolTypeLabel(tool.tool_type)}</td>
                        <td>{tool.spec ?? '—'}</td>
                        <td>{tool.rated_life_shots === null ? '—' : tool.rated_life_shots}</td>
                        <td data-testid={`tool-cumulative-${tool.tool_id}`} title={headline.cumulativeLabel}>
                          {headline.cumulativeValue}
                        </td>
                        <td
                          data-testid={`tool-since-maintenance-${tool.tool_id}`}
                          title={headline.sinceMaintenanceLabel}
                        >
                          {headline.sinceMaintenanceValue}
                        </td>
                        <td data-testid={`tool-rated-status-${tool.tool_id}`}>
                          <StatusBadge tone={ratedLifeStatusTone(status)}>
                            {ratedLifeStatusLabel(status)}
                          </StatusBadge>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`tool-edit-open-${tool.tool_id}`}
                            disabled={!canEdit}
                            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${PRODUCT_MASTER_MODULE} + EDIT จึงจะแก้ไขได้`}
                            onClick={() => {
                              openEdit(tool);
                            }}
                          >
                            แก้ไข
                          </button>{' '}
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`tool-detail-open-${tool.tool_id}`}
                            onClick={() => {
                              openDetail(tool.tool_id);
                            }}
                          >
                            {detailToolId === tool.tool_id ? 'ซ่อนรายละเอียด' : 'รายละเอียด'}
                          </button>
                        </td>
                      </tr>
                      {detailToolId === tool.tool_id ? (
                        <tr data-testid={`tool-detail-row-${tool.tool_id}`}>
                          <td colSpan={8}>
                            <ToolDetailPanel
                              detail={detail}
                              canEdit={canEdit}
                              busy={busy}
                              productIdInput={productIdInput}
                              setProductIdInput={setProductIdInput}
                              productLinkBody={productLinkBody}
                              productLinkFailure={productLinkFailure}
                              submitLinkProduct={submitLinkProduct}
                              workCenterIdInput={workCenterIdInput}
                              setWorkCenterIdInput={setWorkCenterIdInput}
                              workCenterLinkBody={workCenterLinkBody}
                              workCenterLinkFailure={workCenterLinkFailure}
                              submitLinkWorkCenter={submitLinkWorkCenter}
                              maintenancePerformedAt={maintenancePerformedAt}
                              setMaintenancePerformedAt={setMaintenancePerformedAt}
                              maintenanceNote={maintenanceNote}
                              setMaintenanceNote={setMaintenanceNote}
                              maintenanceBody={maintenanceBody}
                              maintenanceFailure={maintenanceFailure}
                              submitMaintenance={submitMaintenance}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

/* ----------------------------------------------------- แผงรายละเอียด --- */

interface ToolDetailPanelProps {
  readonly detail: DetailState | null;
  readonly canEdit: boolean;
  readonly busy: boolean;
  readonly productIdInput: string;
  readonly setProductIdInput: (value: string) => void;
  readonly productLinkBody: { readonly product_id: string } | null;
  readonly productLinkFailure: ToolFailure | null;
  readonly submitLinkProduct: () => void;
  readonly workCenterIdInput: string;
  readonly setWorkCenterIdInput: (value: string) => void;
  readonly workCenterLinkBody: { readonly work_center_id: string } | null;
  readonly workCenterLinkFailure: ToolFailure | null;
  readonly submitLinkWorkCenter: () => void;
  readonly maintenancePerformedAt: string;
  readonly setMaintenancePerformedAt: (value: string) => void;
  readonly maintenanceNote: string;
  readonly setMaintenanceNote: (value: string) => void;
  readonly maintenanceBody: { readonly performed_at: string; readonly note?: string } | null;
  readonly maintenanceFailure: ToolFailure | null;
  readonly submitMaintenance: () => void;
}

function ToolDetailPanel({
  detail,
  canEdit,
  busy,
  productIdInput,
  setProductIdInput,
  productLinkBody,
  productLinkFailure,
  submitLinkProduct,
  workCenterIdInput,
  setWorkCenterIdInput,
  workCenterLinkBody,
  workCenterLinkFailure,
  submitLinkWorkCenter,
  maintenancePerformedAt,
  setMaintenancePerformedAt,
  maintenanceNote,
  setMaintenanceNote,
  maintenanceBody,
  maintenanceFailure,
  submitMaintenance,
}: ToolDetailPanelProps) {
  if (detail === null) {
    return null;
  }

  const products = detail.products;
  const workCenters = detail.workCenters;
  const maintenanceEvents = detail.maintenanceEvents;

  return (
    <div className="cap-card" data-testid="tool-detail-panel">
      {/* ส่วนประกอบที่ผลิตได้ — many-to-many, เสมอเป็นรายการ */}
      <div data-testid="tool-detail-products">
        <h3 className="cap-notice__title">ส่วนประกอบที่เครื่องมือนี้ผลิตได้</h3>
        {products.status === 'loading' ? (
          <p className="cap-notice__text">กำลังโหลด…</p>
        ) : products.status !== 'ready' ? (
          <ToolFailureNotice failure={products} testIdPrefix="tool-detail-products" />
        ) : products.items.length === 0 ? (
          <p className="cap-notice__text">ยังไม่มีส่วนประกอบที่อนุมัติให้ผลิต</p>
        ) : (
          <ul data-testid="tool-detail-products-list">
            {products.items.map((entry) => (
              <li key={entry.product_id} data-testid={`tool-detail-product-${entry.product_id}`}>
                <code className="cap-table__code">{entry.product_code}</code>
              </li>
            ))}
          </ul>
        )}
        <form
          className="cap-form cap-form--inline"
          data-testid="tool-detail-product-add-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitLinkProduct();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">product_id (UUID)</span>
            <input
              className="cap-input"
              data-testid="tool-detail-product-input"
              value={productIdInput}
              autoComplete="off"
              disabled={!canEdit}
              onChange={(event) => {
                setProductIdInput(event.target.value);
              }}
            />
          </label>
          <button
            type="submit"
            className="cap-button cap-button--ghost"
            data-testid="tool-detail-product-submit"
            disabled={busy || !canEdit || productLinkBody === null}
            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${PRODUCT_MASTER_MODULE} + EDIT จึงจะเพิ่มได้`}
          >
            เพิ่ม
          </button>
        </form>
        {productLinkFailure === null ? null : (
          <ToolFailureNotice failure={productLinkFailure} testIdPrefix="tool-detail-product-add" />
        )}
      </div>

      {/* เครื่องจักรที่ใช้ได้ — many-to-many, เสมอเป็นรายการ */}
      <div data-testid="tool-detail-work-centers">
        <h3 className="cap-notice__title">เครื่องจักรที่เครื่องมือนี้ใช้ได้</h3>
        {workCenters.status === 'loading' ? (
          <p className="cap-notice__text">กำลังโหลด…</p>
        ) : workCenters.status !== 'ready' ? (
          <ToolFailureNotice failure={workCenters} testIdPrefix="tool-detail-work-centers" />
        ) : workCenters.items.length === 0 ? (
          <p className="cap-notice__text">ยังไม่มีเครื่องจักรที่บันทึกไว้</p>
        ) : (
          <ul data-testid="tool-detail-work-centers-list">
            {workCenters.items.map((entry) => (
              <li
                key={entry.work_center_id}
                data-testid={`tool-detail-work-center-${entry.work_center_id}`}
              >
                <code className="cap-table__code">{entry.work_center_code}</code>{' '}
                (<code className="cap-table__code">{entry.plant_code}</code>)
              </li>
            ))}
          </ul>
        )}
        <form
          className="cap-form cap-form--inline"
          data-testid="tool-detail-work-center-add-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitLinkWorkCenter();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">work_center_id (UUID)</span>
            <input
              className="cap-input"
              data-testid="tool-detail-work-center-input"
              value={workCenterIdInput}
              autoComplete="off"
              disabled={!canEdit}
              onChange={(event) => {
                setWorkCenterIdInput(event.target.value);
              }}
            />
          </label>
          <button
            type="submit"
            className="cap-button cap-button--ghost"
            data-testid="tool-detail-work-center-submit"
            disabled={busy || !canEdit || workCenterLinkBody === null}
            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${PRODUCT_MASTER_MODULE} + EDIT จึงจะเพิ่มได้`}
          >
            เพิ่ม
          </button>
        </form>
        {workCenterLinkFailure === null ? null : (
          <ToolFailureNotice
            failure={workCenterLinkFailure}
            testIdPrefix="tool-detail-work-center-add"
          />
        )}
      </div>

      {/* ประวัติซ่อมบำรุง — THE HEADLINE ของสองตัวนับ */}
      <div data-testid="tool-detail-maintenance">
        <h3 className="cap-notice__title">ประวัติซ่อมบำรุง</h3>
        {maintenanceEvents.status === 'loading' ? (
          <p className="cap-notice__text">กำลังโหลด…</p>
        ) : maintenanceEvents.status !== 'ready' ? (
          <ToolFailureNotice failure={maintenanceEvents} testIdPrefix="tool-detail-maintenance" />
        ) : maintenanceEvents.items.length === 0 ? (
          <p className="cap-notice__text">ยังไม่มีการซ่อมบำรุงที่บันทึกไว้</p>
        ) : (
          <ul data-testid="tool-detail-maintenance-list">
            {maintenanceEvents.items.map((entry) => (
              <li key={entry.event_id} data-testid={`tool-detail-maintenance-${entry.event_id}`}>
                {entry.performed_at} — {entry.performed_by_employee_id}
                {entry.note === null ? '' : ` — ${entry.note}`}
              </li>
            ))}
          </ul>
        )}
        <form
          className="cap-form cap-form--inline"
          data-testid="tool-detail-maintenance-add-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitMaintenance();
          }}
        >
          <label className="cap-field">
            <span className="cap-field__label">วันที่/เวลาที่ซ่อมจริง</span>
            <input
              className="cap-input"
              type="datetime-local"
              data-testid="tool-detail-maintenance-performed-at"
              value={maintenancePerformedAt}
              disabled={!canEdit}
              onChange={(event) => {
                setMaintenancePerformedAt(event.target.value);
              }}
            />
          </label>
          <label className="cap-field">
            <span className="cap-field__label">หมายเหตุ (ไม่บังคับ)</span>
            <input
              className="cap-input"
              data-testid="tool-detail-maintenance-note"
              value={maintenanceNote}
              maxLength={MAINTENANCE_NOTE_MAX_LENGTH}
              autoComplete="off"
              disabled={!canEdit}
              onChange={(event) => {
                setMaintenanceNote(event.target.value);
              }}
            />
          </label>
          <button
            type="submit"
            className="cap-button cap-button--primary"
            data-testid="tool-detail-maintenance-submit"
            disabled={busy || !canEdit || maintenanceBody === null}
            title={canEdit ? undefined : `ต้องมีสิทธิ์ ${PRODUCT_MASTER_MODULE} + EDIT จึงจะบันทึกได้`}
          >
            บันทึกซ่อมบำรุง
          </button>
        </form>
        {maintenanceFailure === null ? null : (
          <ToolFailureNotice
            failure={maintenanceFailure}
            testIdPrefix="tool-detail-maintenance-add"
          />
        )}
      </div>
    </div>
  );
}
